import { useState } from 'react';
import { useDigests, useDigest } from '@/hooks/useProcessing';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ChevronLeft, Calendar } from 'lucide-react';
import { formatDate } from '@/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { IntervalDigest } from '@/types';

function DigestList({ onSelect }: { onSelect: (digest: IntervalDigest) => void }) {
  const { data: digests, isLoading } = useDigests(10);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (!digests || digests.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">暂无摘要</p>
          <p className="text-sm text-muted-foreground mt-2">
            等待定时任务生成或手动触发处理
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {digests.map((digest: IntervalDigest) => (
        <Card
          key={digest.id}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => onSelect(digest)}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <CardTitle className="text-lg">{digest.title}</CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {formatDate(digest.created_at)}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground line-clamp-2">{digest.summary}</p>
            <div className="flex items-center gap-2 mt-4">
              {digest.categories_covered?.map((cat: string) => (
                <Badge key={cat} variant="secondary">
                  {cat === 'news' ? '新闻' : cat === 'opinion' ? '观点' : cat}
                </Badge>
              ))}
              <span className="text-sm text-muted-foreground ml-auto">
                {digest.tweet_count} 条推文
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function DigestDetail({ digest, onBack }: { digest: IntervalDigest; onBack: () => void }) {
  const { data: fullDigest, isLoading } = useDigest(digest.id);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  const displayDigest = fullDigest || digest;
  
  return (
    <div>
      <Button variant="ghost" onClick={onBack} className="mb-4">
        <ChevronLeft className="h-4 w-4 mr-2" />
        返回列表
      </Button>
      
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <CardTitle className="text-2xl">{displayDigest.title}</CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {formatDate(displayDigest.created_at)}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">摘要</h3>
            <p className="text-muted-foreground">{displayDigest.summary}</p>
          </div>
          
          {/* Key Points */}
          {displayDigest.bullets && displayDigest.bullets.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">要点</h3>
              <ul className="list-disc pl-6 space-y-1">
                {displayDigest.bullets.map((bullet: string, index: number) => (
                  <li key={index} className="text-muted-foreground">{bullet}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Full Content */}
          {displayDigest.full_content && (
            <div className="markdown-content">
              <h3 className="text-lg font-semibold mb-2">详细内容</h3>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayDigest.full_content}
              </ReactMarkdown>
            </div>
          )}
          
          {/* Footer */}
          <div className="flex items-center gap-2 mt-6 pt-6 border-t border-border">
            {displayDigest.categories.map((cat: string) => (
              <Badge key={cat} variant="secondary">
                {cat === 'news' ? '新闻' : cat === 'opinion' ? '观点' : cat}
              </Badge>
            ))}
            <span className="text-sm text-muted-foreground ml-auto">
              基于 {displayDigest.tweet_count} 条推文生成
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function DigestsPage() {
  const [selectedDigest, setSelectedDigest] = useState<IntervalDigest | null>(null);
  
  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">每日摘要</h2>
      
      {selectedDigest ? (
        <DigestDetail
          digest={selectedDigest}
          onBack={() => setSelectedDigest(null)}
        />
      ) : (
        <DigestList onSelect={setSelectedDigest} />
      )}
    </div>
  );
}
